/**
 * Admin lens — scan settings & access (UI_SDD §R.4, DEC-061).
 *
 * v1 is a read-only view of this scan's configuration. This replaces the
 * previously dead "?view=admin" toggle (2026-07-09 audit finding).
 */
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Coins, GitBranch, Layers, Sparkles, Workflow } from 'lucide-react'
import { scansApi } from '../api/scans'
import { AppShell } from '../components/layout/AppShell'
import { Spinner } from '../components/shared/Spinner'
import { TonePill } from '../components/shared/Badge'
import { PageTitle } from '../components/shared/PageTitle'
import { compactNumber, dollars, repoLabel } from '../utils/format'

function SettingRow({
  icon: Icon,
  label,
  sub,
  value,
  on,
}: {
  icon: typeof Sparkles
  label: string
  sub: string
  value?: string
  on?: boolean
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-[14px] border-b border-fill-subtle last:border-0">
      <div className="w-8 h-8 rounded-lg bg-ground border border-hairline flex items-center justify-center flex-none">
        <Icon className="w-4 h-4 text-accent-text" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13px] leading-tight">{label}</div>
        <div className="text-[11px] leading-tight text-muted mt-[2px]">{sub}</div>
      </div>
      {value !== undefined ? (
        <span className="tabular-nums font-semibold text-[12px] text-ink">{value}</span>
      ) : (
        <TonePill tone={on ? 'success' : 'stopped'}>{on ? 'On' : 'Off'}</TonePill>
      )}
    </div>
  )
}

export default function AdminPage() {
  const { scanId } = useParams<{ scanId: string }>()

  const { data: scan, isLoading } = useQuery({
    queryKey: ['scan', scanId],
    queryFn: () => scansApi.get(scanId!),
    enabled: !!scanId,
  })

  return (
    <AppShell scanId={scanId} crumb="Scan settings" active="report">
      <div className="px-8 py-6 max-w-[720px]">
        <PageTitle eyebrow="Admin" className="mb-1">Scan settings & access</PageTitle>
        <p className="text-[13px] text-muted mb-6">
          How this scan was configured. Settings are fixed once a scan starts. Start a new scan to change them.
        </p>

        {isLoading || !scan ? (
          <div className="py-16 flex justify-center"><Spinner /></div>
        ) : (
          <>
            <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
                Source
              </div>
              <SettingRow icon={GitBranch} label={repoLabel(scan.repo_url)} sub={scan.repo_url} value={scan.branch} />
              <SettingRow
                icon={Workflow}
                label="Triggered by"
                sub="How this scan was started"
                value={scan.triggered_by}
              />
            </div>

            <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
                Analysis settings
              </div>
              <SettingRow
                icon={Sparkles}
                label="AI enrichment"
                sub="LLM analysis passes across agents"
                on={scan.use_llm}
              />
              <SettingRow
                icon={Layers}
                label="Sequential mode"
                sub="Agents run one-at-a-time · testing only"
                on={scan.sequential_waves}
              />
              <SettingRow
                icon={Coins}
                label="Token budget"
                sub="Cap on LLM spend for this scan"
                value={scan.token_budget ? compactNumber(scan.token_budget) : 'Unlimited'}
              />
            </div>

            <div className="bg-panel border border-hairline rounded-md overflow-hidden">
              <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
                Usage
              </div>
              <SettingRow
                icon={Coins}
                label="Tokens used"
                sub="Across all agents, the Planner, and the Critic"
                value={compactNumber(scan.total_tokens_used)}
              />
              <SettingRow
                icon={Coins}
                label="Estimated LLM cost"
                sub="Blended-rate estimate for this scan's API usage"
                value={dollars(scan.estimated_llm_cost)}
              />
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}
